import { SCHEMA_NAMES, type Routine } from "instantdb/types";
import { useUser } from "@/hooks/useUser";
import { MAX_FREE_ROUTINES } from "@/config/constants";
import { useResourceAtoms } from "@/hooks/resources/useResourceAtoms";
import { getRoutines } from "instantdb/client/routine";
import { useResourceOrder } from "@/hooks/resources/useResourceOrder";

type RoutinesReturn = {
  routines: Routine[];
  handleCreateRoutine: ({
    healthAreaId,
    dateTime,
  }: { healthAreaId?: string; dateTime?: Date }) => void;
  handleCopyRoutine: (routine: Omit<Routine, "id">) => void;
  handleEditRoutine: (routine: Routine) => void;
  hasFreeRoutines: boolean;
  isLoading: boolean;
};

export const useRoutines = (): RoutinesReturn => {
  const { hasPro, userId } = useUser();

  // Get routines with ordering applied (now in the getRoutines function)
  const { routines: allRoutines, isLoading } = getRoutines(userId);

  const { orderedResources: orderedRoutines, isLoading: isOrderingLoading } =
    useResourceOrder<Routine>({
      slug: SCHEMA_NAMES.routines,
      resources: allRoutines,
    });

  const routines = orderedRoutines.filter((routine) => routine.aiAccepted || !routine.aiGenerated);
  const hasFreeRoutines = allRoutines.length < MAX_FREE_ROUTINES || hasPro;

  const { handleCreateResource, handleCopyResource, handleEditResource } = useResourceAtoms({
    slug: SCHEMA_NAMES.routines,
    canCreate: hasFreeRoutines,
  });

  return {
    routines: routines,
    handleCreateRoutine: handleCreateResource,
    handleCopyRoutine: handleCopyResource,
    handleEditRoutine: handleEditResource,
    hasFreeRoutines,
    isLoading: isLoading || isOrderingLoading,
  };
};
